import type { Descendant, Value } from 'platejs'
import type {
  NoteAnnotation,
  NoteContent,
} from '~publish/hooks/notes/noteContent'
export type { NoteContent } from '~publish/hooks/notes/noteContent'

const MARKS = ['bold', 'italic', 'strikethrough'] as const

/** The one heading level a Note carries. Post details titles Notes with an h2. */
export const HEADING_KEY = 'h3'

type BlockAnnotationType = 'heading' | 'bulletedListItem' | 'orderedListItem'
type ListAnnotationType = Exclude<BlockAnnotationType, 'heading'>

const LIST_KEYS: Record<ListAnnotationType, string> = {
  bulletedListItem: 'ul',
  orderedListItem: 'ol',
}
const LIST_TYPES = new Map<unknown, ListAnnotationType>([
  ['ul', 'bulletedListItem'],
  ['ol', 'orderedListItem'],
])

const INLINE_TYPES = new Set<unknown>(['a', 'mention'])

const isBlockAnnotationType = (type: string): type is BlockAnnotationType =>
  type === 'heading' ||
  type === 'bulletedListItem' ||
  type === 'orderedListItem'

const hasChildren = (
  node: Descendant,
): node is Descendant & { children: Descendant[] } =>
  'children' in node && Array.isArray(node.children)

const getBreakFormatting = (node: Descendant): Record<string, unknown> => {
  if (
    !('noteBreakFormatting' in node) ||
    typeof node.noteBreakFormatting !== 'object' ||
    node.noteBreakFormatting === null
  )
    return {}
  const formatting = node.noteBreakFormatting
  return {
    bold: 'bold' in formatting ? formatting.bold : undefined,
    italic: 'italic' in formatting ? formatting.italic : undefined,
    strikethrough:
      'strikethrough' in formatting ? formatting.strikethrough : undefined,
    url: 'url' in formatting ? formatting.url : undefined,
  }
}

const rangeAnnotation = (
  type: NoteAnnotation['type'],
  text: string,
  start: number,
  end: number,
  url = '',
): NoteAnnotation => ({
  type,
  content: text.slice(start, end),
  text: text.slice(start, end),
  indices: [start, end],
  url,
})

// Line breaks alone do not separate a mark, the same rule the backend applies.
const rangesTouch = (text: string, end: number, start: number): boolean =>
  /^\n*$/.test(text.slice(end, start))

const mergeMarks = (
  text: string,
  annotations: NoteAnnotation[],
): NoteAnnotation[] => {
  const result = annotations.filter(
    (annotation) =>
      annotation.type === 'mention' || isBlockAnnotationType(annotation.type),
  )
  for (const type of ['url', ...MARKS] as const) {
    const ranges = annotations
      .filter((annotation) => annotation.type === type)
      .toSorted((a, b) => a.indices[0] - b.indices[0])
    for (const annotation of ranges) {
      const previous = result.at(-1)
      if (
        previous?.type === type &&
        // Two links merge only when they point at the same URL.
        (type !== 'url' || previous.url === annotation.url) &&
        rangesTouch(text, previous.indices[1], annotation.indices[0])
      ) {
        result[result.length - 1] = rangeAnnotation(
          type,
          text,
          previous.indices[0],
          Math.max(previous.indices[1], annotation.indices[1]),
          annotation.url,
        )
      } else {
        result.push(annotation)
      }
    }
  }
  // The backend stores [start, end, type] order, so a save round-trips exactly.
  return result.toSorted(
    (left, right) =>
      left.indices[0] - right.indices[0] ||
      left.indices[1] - right.indices[1] ||
      left.type.localeCompare(right.type),
  )
}

/** One line of Note text and the node whose break formatting follows it. */
type NoteLine = {
  node: Descendant
  children: Descendant[]
  blockType?: BlockAnnotationType
}

const collectLines = (nodes: Descendant[], lines: NoteLine[]): void => {
  for (const node of nodes) {
    if (!hasChildren(node)) {
      lines.push({ node, children: [node] })
      continue
    }
    const listType = LIST_TYPES.get(node.type)
    if (listType) {
      for (const item of node.children) collectListItem(item, listType, lines)
    } else {
      lines.push({
        node,
        children: node.children,
        blockType: node.type === HEADING_KEY ? 'heading' : undefined,
      })
    }
  }
}

// Notes store flat lists, so a nested list becomes sibling lines of its own
// type, in place. Each block inside an item, which paste can produce, gives
// its own line so no text runs together; only text and inline elements share
// a line.
const collectListItem = (
  item: Descendant,
  listType: ListAnnotationType,
  lines: NoteLine[],
): void => {
  if (!hasChildren(item)) return
  if (LIST_TYPES.has(item.type)) {
    collectLines([item], lines)
    return
  }
  let own: NoteLine | undefined
  let loose: NoteLine | undefined
  for (const child of item.children) {
    if (hasChildren(child) && LIST_TYPES.has(child.type)) {
      collectLines([child], lines)
      loose = undefined
    } else if (hasChildren(child) && !INLINE_TYPES.has(child.type)) {
      own = { node: child, children: child.children, blockType: listType }
      lines.push(own)
      loose = undefined
    } else {
      if (!loose) {
        loose = { node: child, children: [], blockType: listType }
        lines.push(loose)
      }
      loose.children.push(child)
      own = loose
    }
  }
  if (own) own.node = item
}

export function fromNoteContent({ text, annotations }: NoteContent): Value {
  if (!text) return [{ type: 'p', children: [{ text: '' }] }]

  const blocks: Value = []
  let paragraphStart = 0
  const lines = text.split('\n')
  for (const line of lines) {
    const paragraphEnd = paragraphStart + line.length
    const mentions = annotations.filter(
      (annotation) =>
        annotation.type === 'mention' &&
        annotation.indices[0] >= paragraphStart &&
        annotation.indices[1] <= paragraphEnd,
    )
    const boundaries = new Set([paragraphStart, paragraphEnd])
    for (const annotation of annotations) {
      const [start, end] = annotation.indices
      if (start > paragraphStart && start < paragraphEnd) boundaries.add(start)
      if (end > paragraphStart && end < paragraphEnd) boundaries.add(end)
    }
    const offsets = [...boundaries].toSorted((left, right) => left - right)
    const children: Descendant[] = []
    for (let index = 0; index < offsets.length - 1; index += 1) {
      const start = offsets[index]
      const end = offsets[index + 1]
      const mention = mentions.find(
        (item) => item.indices[0] === start && item.indices[1] === end,
      )
      const marks = Object.fromEntries(
        MARKS.filter((mark) =>
          annotations.some(
            (item) =>
              item.type === mark &&
              item.indices[0] <= start &&
              item.indices[1] >= end,
          ),
        ).map((mark) => [mark, true]),
      )
      if (mention) {
        const label = text.slice(start, end)
        children.push({
          type: 'mention',
          trigger: label[0] === '@' ? '@' : '',
          value: label[0] === '@' ? label.slice(1) : label,
          serviceEntityId: mention.serviceEntityId,
          displayValue: mention.displayValue,
          children: [{ text: '', ...marks }],
        })
      } else {
        const link = annotations.find(
          (item) =>
            item.type === 'url' &&
            item.indices[0] <= start &&
            item.indices[1] >= end,
        )
        const leaf = { text: text.slice(start, end), ...marks }
        const previous = children.at(-1)
        if (
          link &&
          previous &&
          hasChildren(previous) &&
          previous.type === 'a' &&
          previous.url === link.url
        ) {
          previous.children.push(leaf)
        } else if (link) {
          children.push({ type: 'a', url: link.url, children: [leaf] })
        } else {
          children.push(leaf)
        }
      }
    }
    const breakFormatting: Record<string, boolean | string> =
      Object.fromEntries(
        MARKS.filter((mark) =>
          annotations.some(
            (annotation) =>
              annotation.type === mark &&
              annotation.indices[0] <= paragraphEnd &&
              annotation.indices[1] >= paragraphEnd + 1,
          ),
        ).map((mark) => [mark, true]),
      )
    const breakLink = annotations.find(
      (annotation) =>
        annotation.type === 'url' &&
        annotation.indices[0] <= paragraphEnd &&
        annotation.indices[1] >= paragraphEnd + 1,
    )
    if (breakLink) breakFormatting.url = breakLink.url
    const lineProps =
      paragraphEnd < text.length && Object.keys(breakFormatting).length
        ? { noteBreakFormatting: breakFormatting }
        : {}
    const lineChildren = children.length ? children : [{ text: '' }]
    const blockType = annotations.find(
      (annotation) =>
        annotation.indices[0] === paragraphStart &&
        annotation.indices[1] === paragraphEnd &&
        isBlockAnnotationType(annotation.type),
    )?.type
    if (blockType === 'bulletedListItem' || blockType === 'orderedListItem') {
      const listKey = LIST_KEYS[blockType]
      const item = {
        type: 'li',
        ...lineProps,
        children: [{ type: 'lic', children: lineChildren }],
      }
      const previous = blocks.at(-1)
      // Consecutive lines of one list type form one list.
      if (previous?.type === listKey) previous.children.push(item)
      else blocks.push({ type: listKey, children: [item] })
    } else {
      blocks.push({
        type: blockType === 'heading' ? HEADING_KEY : 'p',
        ...lineProps,
        children: lineChildren,
      })
    }
    paragraphStart = paragraphEnd + 1
  }
  return blocks
}

export function toNoteContent(value: Value): NoteContent {
  let text = ''
  const annotations: NoteAnnotation[] = []
  const append = (
    content: string,
    marks: Record<string, unknown>,
    url = '',
  ): void => {
    const start = text.length
    text += content
    if (!content) return
    for (const mark of MARKS) {
      if (marks[mark] === true)
        annotations.push(rangeAnnotation(mark, text, start, text.length))
    }
    if (url)
      annotations.push(rangeAnnotation('url', text, start, text.length, url))
  }
  const visit = (node: Descendant, inheritedUrl = ''): void => {
    if ('text' in node && typeof node.text === 'string') {
      append(node.text, node, inheritedUrl)
      return
    }
    if (!hasChildren(node)) return
    if (node.type === 'mention' && typeof node.value === 'string') {
      const label = `${typeof node.trigger === 'string' ? node.trigger : '@'}${node.value}`
      const start = text.length
      append(label, node.children[0] ?? {})
      annotations.push({
        ...rangeAnnotation('mention', text, start, text.length),
        ...(typeof node.serviceEntityId === 'string'
          ? { serviceEntityId: node.serviceEntityId }
          : {}),
        ...(typeof node.displayValue === 'string'
          ? { displayValue: node.displayValue }
          : {}),
      })
      return
    }
    const url =
      node.type === 'a' && typeof node.url === 'string'
        ? node.url
        : inheritedUrl
    for (const child of node.children) visit(child, url)
  }
  const lines: NoteLine[] = []
  collectLines(value, lines)
  for (const [index, line] of lines.entries()) {
    if (index > 0) {
      const breakFormatting = getBreakFormatting(lines[index - 1].node)
      append(
        '\n',
        breakFormatting,
        typeof breakFormatting.url === 'string' ? breakFormatting.url : '',
      )
    }
    const start = text.length
    for (const child of line.children) visit(child)
    // A block annotation covers one whole line of text, and a soft break
    // (Shift+Enter, a pasted <br>) leaves a newline inside this one, so each
    // part gets its own. An empty part carries none, so an empty item is plain.
    if (line.blockType) {
      let partStart = start
      for (const part of text.slice(start).split('\n')) {
        const partEnd = partStart + part.length
        if (part)
          annotations.push(
            rangeAnnotation(line.blockType, text, partStart, partEnd),
          )
        partStart = partEnd + 1
      }
    }
  }
  return { text, annotations: mergeMarks(text, annotations) }
}
